import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DocumentKind, LearnerViewInPlay, SessionDocumentsResponse } from "@pushback-and-chaos/contracts";
import type { RenderPlan } from "@pushback-and-chaos/documents";
import { apiClient } from "../data/transport.js";
import { CARD_H_REM, CARD_W_REM, FALLBACK_BOUNDS, landingPlaces, tiltFor, toPlace, toSpot, type CardSize, type DeskBounds, type DeskPlace } from "./landing.js";
import { usePointerDrag, remBase } from "../ui/use-drag.js";
import { KindIcon } from "./kind-icon.js";

/**
 * THE DESK — a real desk, not a shelf (organization pass, 2026-08-28; desk
 * pass, 2026-09-03; re-sited for the Storyteller layout, 2026-09-17).
 *
 * A document has a PLACE on the desk — fractions of the desk's free range —
 * decided once when it is delivered (landing.ts) and changed only by the
 * learner dragging it. Opening, closing or dragging any other card never moves
 * it or changes its tilt: nothing here is derived from what else is on the
 * desk. Because a place is a fraction, the desk needs no scroll and no
 * clamp-back — the card is inside the desk at every window size by
 * construction, including when the panel's own container queries shrink the
 * paper. A newly delivered document slides in from beyond the desk's edge,
 * once, the first time this browser sees it.
 *
 * Every card is freely draggable. A drop inside the desk commits a new place
 * (persisted by the play surface, and the card comes to the top); a drop
 * OUTSIDE the desk — over the conversation or over the scene — opens the
 * document as a movable window there, at the point it was let go. A plain
 * click, or Enter on a focused card, still opens it at the default spot; the
 * gesture is never the only door.
 *
 * What changed when the desk moved to the lower right: the drop decision is
 * real two-dimensional hit testing against the desk's own rectangle. The old
 * `pointerX > desk.right` assumed the desk was the left-hand column and that
 * everything to its right was the narration; there are two panels over there
 * now, and the desk is beside them rather than before them.
 *
 * The card face comes from `GET /sessions/:id/documents`, which carries each
 * document's real title and preview. NOT from the session state's
 * `documentsDelivered[].title`: `publishCase` synthesizes that as
 * `"financial table (doc_board_pack_extract)"` — a lowercased kind plus a raw
 * snake_case identifier — because `CaseBibleDocumentSchema` has no title
 * field. And NOT from the render plan either: fetching a plan IS a document
 * open under ADR 0008, and the desk labelling its cards must not read every
 * document on the learner's behalf. The plan is fetched only when a card is
 * actually opened.
 */
export interface DeskDocument {
  readonly id: string;
  readonly kind: DocumentKind;
  readonly title: string;
  readonly preview: string;
}

/** Stagger between cards that arrive in the same delivery, in ms. */
const ARRIVAL_STAGGER_MS = 90;

/** Above every resting card, whatever the count; the dragged card sits above that. */
const HOVER_Z = 900;
const DRAG_Z = 1000;

export function deskCardsQueryKey(sessionId: string, docIds: readonly string[]): readonly [string, string, string] {
  return ["deskCards", sessionId, docIds.join("|")];
}

/**
 * The desk's cards. Null while the list is still arriving — a card is never
 * shown without its title, so nothing renders until the faces are known.
 * Keyed on the delivered ids so a new delivery refetches the list.
 */
export function useDeskCards(sessionId: string, documents: LearnerViewInPlay["documents"]): readonly DeskDocument[] | null {
  const client = apiClient();
  const ids = documents.map((doc) => doc.id);
  const query = useQuery({
    queryKey: deskCardsQueryKey(sessionId, ids),
    queryFn: () => client.getDocuments(sessionId),
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
    enabled: ids.length > 0,
  });
  const list: SessionDocumentsResponse | undefined = query.data;
  return useMemo<readonly DeskDocument[] | null>(() => {
    if (documents.length === 0) return [];
    if (list === undefined) return null;
    const byId = new Map(list.map((entry) => [entry.docId, entry]));
    return documents.flatMap((doc) => {
      const face = byId.get(doc.id);
      return face === undefined ? [] : [{ id: doc.id, kind: doc.kind, title: face.title, preview: face.preview }];
    });
  }, [documents, list]);
}

/**
 * The full render plan of ONE document — fetched only when it is opened,
 * because the fetch IS the open (ADR 0008). A null id fetches nothing: the
 * desk must never record a read for a card whose face has not arrived.
 */
export function useDocumentPlan(sessionId: string, docId: string | null): RenderPlan | null {
  const client = apiClient();
  const query = useQuery({
    queryKey: ["renderPlan", sessionId, docId ?? ""],
    queryFn: () => client.getDocument(sessionId, docId ?? "") as Promise<RenderPlan>,
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
    enabled: docId !== null && docId.length > 0,
  });
  return query.data ?? null;
}

/** The card's size in rem, as the desk's own tokens resolve it right now. */
function cardSizeOf(stack: HTMLElement): CardSize {
  const style = getComputedStyle(stack);
  const base = remBase();
  const w = Number.parseFloat(style.getPropertyValue("--pcs-card-w")) / base;
  const h = Number.parseFloat(style.getPropertyValue("--pcs-card-h")) / base;
  return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0 ? { w, h } : { w: CARD_W_REM, h: CARD_H_REM };
}

/** The stack's inner size in rem, or the design fallback where it cannot be measured (first paint, jsdom). */
function boundsOf(stack: HTMLElement, card: CardSize): DeskBounds {
  const rect = stack.getBoundingClientRect();
  const base = remBase();
  const measured: DeskBounds = { w: rect.width / base, h: rect.height / base };
  return measured.w < card.w || measured.h < card.h ? FALLBACK_BOUNDS : measured;
}

/**
 * A pixel point over the stack -> a place. Shared by the desk's own drops and
 * by an open document minimising back onto the desk, so both land inside it by
 * the same rule.
 */
export function placeOnStack(stack: HTMLElement, cardLeftPx: number, cardTopPx: number): DeskPlace {
  const rect = stack.getBoundingClientRect();
  const base = remBase();
  const card = cardSizeOf(stack);
  return toPlace({ x: (cardLeftPx - rect.left) / base, y: (cardTopPx - rect.top) / base }, boundsOf(stack, card), card);
}

export function DocumentDesk({
  sessionId,
  cards,
  openIds,
  places,
  raisedIds,
  seenIds,
  onOpen,
  onOpenAt,
  onPlace,
  onSeen,
}: {
  sessionId: string;
  /** Null while the card faces are still arriving. */
  cards: readonly DeskDocument[] | null;
  /** Documents open as a window elsewhere; their card is off the desk. */
  openIds: readonly string[];
  /** Places the LEARNER chose, by document id. A card without one sits where it landed. */
  places: Readonly<Record<string, DeskPlace>>;
  /** Cards the learner has put down, oldest first; each sits above every card that was never moved. */
  raisedIds: readonly string[];
  /** Documents this browser has already seen on the desk; anything else slides in. */
  seenIds: readonly string[];
  onOpen: (doc: DeskDocument) => void;
  /** The card was let go OFF the desk — open its document as a window there. */
  onOpenAt: (doc: DeskDocument, clientX: number, clientY: number) => void;
  onPlace: (id: string, place: DeskPlace) => void;
  /** These documents have now been shown; remember them so they do not slide in twice. */
  onSeen: (ids: readonly string[]) => void;
}): ReactNode {
  const stackRef = useRef<HTMLDivElement | null>(null);
  const [geometry, setGeometry] = useState<{ readonly bounds: DeskBounds; readonly card: CardSize }>({ bounds: FALLBACK_BOUNDS, card: { w: CARD_W_REM, h: CARD_H_REM } });
  /** Cards sliding in during this mount, with their position in the delivery for the stagger. */
  const [arriving, setArriving] = useState<ReadonlyMap<string, number>>(() => new Map());

  // Places are fractions; the desk's size and the paper's size only decide
  // where a fraction falls, so re-resolve whenever either changes.
  useEffect(() => {
    const stack = stackRef.current;
    if (stack === null) return;
    const read = (): void => {
      const card = cardSizeOf(stack);
      const bounds = boundsOf(stack, card);
      setGeometry((previous) =>
        previous.card.w === card.w && previous.card.h === card.h && previous.bounds.w === bounds.w && previous.bounds.h === bounds.h
          ? previous
          : { bounds, card },
      );
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(stack);
    return () => { observer.disconnect(); };
  }, []);

  const ids = useMemo(() => (cards ?? []).map((card) => card.id), [cards]);
  const landings = useMemo(() => landingPlaces(sessionId, ids), [sessionId, ids]);

  // ARRIVAL. A document this browser has not shown before slides onto the
  // desk; from then on it is simply there. Remembered by the play surface (per
  // session, in the learner's own browser), so a reload does not replay it and
  // a cleared storage replays it exactly once.
  useEffect(() => {
    if (cards === null) return;
    const unseen = cards.filter((card) => !seenIds.includes(card.id)).map((card) => card.id);
    if (unseen.length === 0) return;
    setArriving((previous) => {
      const next = new Map(previous);
      unseen.forEach((id, order) => {
        if (!next.has(id)) next.set(id, order);
      });
      return next;
    });
    onSeen(unseen);
  }, [cards, seenIds, onSeen]);

  const onArrived = useCallback((id: string) => {
    setArriving((previous) => {
      if (!previous.has(id)) return previous;
      const next = new Map(previous);
      next.delete(id);
      return next;
    });
  }, []);

  const visible = cards ?? [];
  const onDesk = visible.filter((card) => !openIds.includes(card.id));

  return (
    <div className="pcs-cards" data-region="desk-stack" ref={stackRef} aria-label="Documents you have">
      {cards !== null && onDesk.length === 0 ? (
        <p className="pcs-quiet pcs-desk-empty">{cards.length === 0 ? "Nothing on the desk yet." : "Everything you have is open."}</p>
      ) : null}
      {visible.map((card, index) => {
        if (openIds.includes(card.id)) return null;
        const place = places[card.id] ?? landings.get(card.id) ?? { fx: 0, fy: 0 };
        const raised = raisedIds.indexOf(card.id);
        const order = arriving.get(card.id);
        return (
          <DeskCard
            key={card.id}
            card={card}
            rest={toSpot(place, geometry.bounds, geometry.card)}
            tilt={tiltFor(sessionId, card.id)}
            // z-order is delivery order — a later document lands on top of an
            // earlier one — with anything the learner has put down above all of
            // that, most recent on top. Never the on-desk index, so opening a
            // card leaves every other card's layer where it was.
            zIndex={raised === -1 ? index + 1 : visible.length + 1 + raised}
            arrivalOrder={order ?? null}
            stackRef={stackRef}
            onArrived={() => { onArrived(card.id); }}
            onOpen={() => { onOpen(card); }}
            onOpenAt={(x, y) => { onOpenAt(card, x, y); }}
            onPlace={(next) => { onPlace(card.id, next); }}
          />
        );
      })}
    </div>
  );
}

function DeskCard({
  card,
  rest,
  tilt,
  zIndex,
  arrivalOrder,
  stackRef,
  onArrived,
  onOpen,
  onOpenAt,
  onPlace,
}: {
  card: DeskDocument;
  rest: { readonly x: number; readonly y: number };
  tilt: number;
  zIndex: number;
  /** Non-null while the card is sliding onto the desk: its position in the delivery, for the stagger. */
  arrivalOrder: number | null;
  stackRef: { readonly current: HTMLDivElement | null };
  onArrived: () => void;
  onOpen: () => void;
  onOpenAt: (clientX: number, clientY: number) => void;
  onPlace: (place: DeskPlace) => void;
}): ReactNode {
  // While dragging the card goes position:fixed so it escapes the desk's
  // clipping and can visibly cross into the conversation or the scene.
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);
  const grabOffset = useRef({ x: 0, y: 0 });

  const startDrag = usePointerDrag({
    onMove: (_dx, _dy, e) => {
      setDrag({ x: e.clientX - grabOffset.current.x, y: e.clientY - grabOffset.current.y });
    },
    onEnd: (end) => {
      setDrag(null);
      if (!end.moved) {
        onOpen();
        return;
      }
      const stack = stackRef.current;
      if (stack === null) return;
      /*
       * REAL HIT TESTING. Inside the desk's own rectangle the card is put
       * down; anywhere else — the conversation, the scene, the gap between
       * them — the document opens as a window at the point it was let go.
       * The old rule compared the pointer with one edge of a left-hand desk;
       * there is no such edge to compare with now.
       */
      const desk = stack.closest('[data-region="desk"]');
      const bounds = desk?.getBoundingClientRect();
      const onTheDesk =
        bounds !== undefined && end.clientX >= bounds.left && end.clientX <= bounds.right && end.clientY >= bounds.top && end.clientY <= bounds.bottom;
      if (!onTheDesk) {
        onOpenAt(end.clientX, end.clientY);
        return;
      }
      onPlace(placeOnStack(stack, end.clientX - grabOffset.current.x, end.clientY - grabOffset.current.y));
    },
  });

  const arriving = drag === null && arrivalOrder !== null;

  // The tilt and the slide-in distance ride on custom properties so the
  // arrival keyframes end on exactly the card's resting transform — no jump
  // when the animation hands over to the inline style.
  const style: CSSProperties & Record<"--pcs-tilt" | "--pcs-land-dx", string> = {
    left: drag === null ? `${String(rest.x)}rem` : `${String(drag.x)}px`,
    top: drag === null ? `${String(rest.y)}rem` : `${String(drag.y)}px`,
    zIndex: drag === null ? zIndex : DRAG_Z,
    animationDelay: arriving ? `${String(arrivalOrder * ARRIVAL_STAGGER_MS)}ms` : undefined,
    "--pcs-tilt": `${String(tilt)}deg`,
    // Far enough left to start beyond the desk's edge wherever the card rests.
    "--pcs-land-dx": `${String(-(rest.x + CARD_W_REM + 3))}rem`,
  };

  return (
    <button
      type="button"
      data-desk-card={card.id}
      onPointerDown={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        grabOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        startDrag(e);
      }}
      // A pointer click is handled by the drag plumbing above (a press that
      // never moved opens). This is the keyboard's door: Enter or Space on a
      // focused card synthesizes a click with detail 0 and no pointer events.
      onClick={(e) => { if (e.detail === 0) onOpen(); }}
      onAnimationEnd={arriving ? onArrived : undefined}
      title={`${card.title} — ${card.preview}`}
      aria-label={`${card.title}. ${card.preview}`}
      className={[
        "pcs-card",
        drag === null ? `absolute transition-transform duration-[var(--pc-dur)] ease-pc hover:z-[${String(HOVER_Z)}]` : "fixed cursor-grabbing",
        arriving ? "pcs-card-land" : "",
      ].join(" ")}
      style={style}
    >
      <span className="pcs-card-kind">
        <span>{kindLabel(card.kind)}</span>
        <KindIcon kind={card.kind} />
      </span>
      <span className="pcs-card-title">{card.title}</span>
      <span className="pcs-card-rule" aria-hidden="true" />
      <span className="pcs-card-byline">{card.preview}</span>
    </button>
  );
}

/**
 * A card's kind, in the fiction's own words. NEVER the bare `DocumentKind` —
 * `FINANCIAL_TABLE` on a card face is exactly the enum leak Law 2 bans, and
 * the leak test scans for it.
 */
export function kindLabel(kind: DocumentKind): string {
  switch (kind) {
    case "EMAIL":
      return "message";
    case "FINANCIAL_TABLE":
      return "figures";
    case "DATAROOM_INDEX":
      return "index";
    case "POSTIT":
      return "note to self";
    case "MEMO":
      return "memo";
    case "EXHIBIT":
      return "exhibit";
    case "TRANSCRIPT":
      return "transcript";
    case "SLIDE":
      return "slide";
    case "ENVIRONMENT":
      return "what you saw";
    default:
      return "paper";
  }
}
